import React, { useEffect, useRef } from "react";

import { TickIcon } from "@/assets/icons";
import { cn } from "@/lib/cn";

interface CheckboxProps {
  id: string;
  name?: string;
  checked: boolean;
  disabled?: boolean;
  label: string;
  labelVisible?: boolean;
  onChange?: (value: boolean) => void;
  size?: number;
  className?: string;
  /** Some but not all of what the checkbox stands for is checked; announced as "mixed". */
  indeterminate?: boolean;
}
export const Checkbox = (props: CheckboxProps) => {
  const {
    id,
    name,
    checked,
    disabled = false,
    label,
    labelVisible = true,
    onChange,
    size = 20,
    className,
    indeterminate = false,
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const mixed = indeterminate && !checked;
  const filled = checked || mixed;

  // The mixed state exists only as a DOM property, never as an attribute
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = mixed;
    }
  }, [mixed]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.currentTarget.checked);
    }
  };

  return (
    // Native input that is absolutely positioned must be wrapped in a relative parent,
    // othervise focusing on checkbox may cause unwanted scroll to top.
    <div className="relative">
      {/* Native input is hidden from UI but still focusable & maintains keyboard navigation */}
      <input
        id={id}
        name={name}
        aria-label={label}
        type="checkbox"
        ref={inputRef}
        aria-checked={mixed ? "mixed" : checked}
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
        className="peer absolute opacity-0"
      />
      <label
        htmlFor={id}
        // The box size is a prop, so it reaches the classes through a custom property
        style={{ "--checkbox-size": `${size}px` } as React.CSSProperties}
        className={cn(
          "relative m-0 flex h-[34px] w-fit cursor-pointer items-center transition-colors",
          "before:inline-block before:size-(--checkbox-size) before:rounded-sm before:border before:transition-colors before:content-['']",
          "before:ring-offset-background peer-focus-visible:before:ring-2 peer-focus-visible:before:ring-ring peer-focus-visible:before:ring-offset-2",
          filled
            ? "before:border-primary before:bg-primary"
            : "before:border-muted-foreground before:bg-background",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
      >
        <span
          className={cn(
            "absolute flex size-(--checkbox-size) items-center justify-center text-primary-foreground [&_svg]:w-[70%]",
            !filled && "opacity-0",
          )}
        >
          {/* The "some selected" mark: a bar across the filled box, drawn rather than typed */}
          {mixed ? <span className="h-0.5 w-[60%] rounded-[1px] bg-current" /> : <TickIcon />}
        </span>
        <span className={cn("ml-2 text-sm text-foreground", !labelVisible && "sr-only")}>
          {label}
        </span>
      </label>
    </div>
  );
};
